import { useEffect, useRef, useState } from 'react';
import { fetchArchives, type Archive } from '../../api/archives';
import { importEditFromUrl } from '../../api/importEdit';
import { usePlayback } from '../../state/playback';
import { useTimeline } from '../../state/timeline';
import './ImportEditDialog.css';

/**
 * Paste a pan.do/ra Edit URL and drop its clips onto the target video track at
 * the current playhead. Fetches the (cached) archives list itself, so the
 * caller only needs to pass the track it should import into.
 */
export default function ImportEditDialog({
  trackId,
  onClose,
}: {
  trackId?: string;
  onClose: () => void;
}) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const archivesRef = useRef<Archive[] | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    void fetchArchives().then((a) => {
      archivesRef.current = a;
    });
    inputRef.current?.focus();
  }, []);

  async function run() {
    const trimmed = url.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const archives = archivesRef.current ?? (await fetchArchives());
      archivesRef.current = archives;
      const result = await importEditFromUrl(trimmed, archives);
      if (result.clips.length === 0) {
        setError('That edit has no importable clips.');
        setBusy(false);
        return;
      }
      const at = usePlayback.getState().currentTime;
      useTimeline.getState().insertEditClips(result.clips, at, trackId);
      const skipped = result.skipped > 0 ? `, ${result.skipped} skipped` : '';
      setDone(`Imported ${result.clips.length} clip${result.clips.length === 1 ? '' : 's'}${skipped}.`);
      setTimeout(onClose, 900);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed.');
      setBusy(false);
    }
  }

  return (
    <div className="import-overlay" onClick={() => !busy && onClose()}>
      <div className="import-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="import-head">
          <h2>Import edit</h2>
          <button className="ghost icon" onClick={onClose} title="Close">
            ×
          </button>
        </div>
        <p className="import-hint">
          Paste a pan.do/ra edit URL. Its clips are added to this track at the playhead.
        </p>
        <input
          ref={inputRef}
          className="import-url"
          placeholder="https://pad.ma/edits/user:name"
          value={url}
          disabled={busy}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void run();
            if (e.key === 'Escape' && !busy) onClose();
          }}
        />
        {error && <p className="import-error">{error}</p>}
        {done && <p className="import-done">{done}</p>}
        <div className="import-actions">
          <button className="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="primary" onClick={() => void run()} disabled={busy || !url.trim()}>
            {busy ? 'Importing…' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
}
